/**
 * Pure operational-rollout domain (Phase 1b.8F).
 * Per-surface promotion state — separate from policy document selection.
 */

import {
  COMPATIBILITY_SURFACES,
  type CompatibilitySurface,
} from './compatibility.domain.js';

/** Surfaces eligible for Phase 1b.8F config promotion (runtime excluded). */
export const CONFIGURATION_PROMOTION_SURFACES = [
  'audit',
  'preview',
  'validation',
  'save',
] as const satisfies readonly CompatibilitySurface[];

export type ConfigurationPromotionSurface =
  (typeof CONFIGURATION_PROMOTION_SURFACES)[number];

/** Preferred risk progression (advisory for audit/preview; soft-required later). */
export const PREFERRED_PROMOTION_ORDER: readonly ConfigurationPromotionSurface[] = [
  'audit',
  'preview',
  'validation',
  'save',
];

export type CompatibilityPolicyOperationalApplication = Record<
  CompatibilitySurface,
  boolean
>;

export const ALL_DISABLED_OPERATIONAL_APPLICATION: CompatibilityPolicyOperationalApplication =
  {
    audit: false,
    preview: false,
    validation: false,
    save: false,
    runtime: false,
  };

export type PromotionCheckStatus = 'pass' | 'warn' | 'fail';

export type PromotionReadinessCheck = {
  code: string;
  status: PromotionCheckStatus;
  summary: string;
  remediation?: string;
};

/** Code-defined initial thresholds (documented; tunable later). */
export const PROMOTION_THRESHOLDS = {
  previewMaxAgeMs: 24 * 60 * 60 * 1000,
  telemetryWindowDays: {
    audit: 0,
    preview: 0,
    validation: 7,
    save: 7,
  } as Record<ConfigurationPromotionSurface, number>,
  requireTelemetryHealthy: {
    audit: true,
    preview: false,
    validation: true,
    save: true,
  } as Record<ConfigurationPromotionSurface, boolean>,
  /** Zero observations acceptable for low-risk surfaces. */
  minTelemetryEvents: {
    audit: 0,
    preview: 0,
    validation: 1,
    save: 1,
  } as Record<ConfigurationPromotionSurface, number>,
  maxUnknownFail: {
    audit: 100,
    preview: 100,
    validation: 25,
    save: 0,
  } as Record<ConfigurationPromotionSurface, number>,
  /** Newly-blocked entities: save fails readiness if > 0 without explicit review ack. */
  maxNewlyBlockedWithoutReview: {
    audit: Number.POSITIVE_INFINITY,
    preview: Number.POSITIVE_INFINITY,
    validation: 50,
    save: 0,
  } as Record<ConfigurationPromotionSurface, number>,
} as const;

export function isConfigurationPromotionSurface(
  surface: string,
): surface is ConfigurationPromotionSurface {
  return (CONFIGURATION_PROMOTION_SURFACES as readonly string[]).includes(surface);
}

export function isCompatibilitySurface(value: string): value is CompatibilitySurface {
  return (COMPATIBILITY_SURFACES as readonly string[]).includes(value);
}

export function normalizeOperationalApplication(
  input: Partial<CompatibilityPolicyOperationalApplication> | null | undefined,
): CompatibilityPolicyOperationalApplication {
  const base = { ...ALL_DISABLED_OPERATIONAL_APPLICATION };
  if (!input) return base;
  for (const surface of COMPATIBILITY_SURFACES) {
    if (input[surface] === true) base[surface] = true;
  }
  // Never honor runtime from malformed/hostile input in this phase.
  base.runtime = false;
  return base;
}

export function parseOperationalApplicationFromRow(row: {
  audit: number | boolean;
  preview: number | boolean;
  validation: number | boolean;
  save_surface: number | boolean;
  runtime: number | boolean;
}): CompatibilityPolicyOperationalApplication {
  const asBool = (v: number | boolean) => v === true || v === 1;
  return normalizeOperationalApplication({
    audit: asBool(row.audit),
    preview: asBool(row.preview),
    validation: asBool(row.validation),
    save: asBool(row.save_surface),
    runtime: asBool(row.runtime),
  });
}

export function surfaceEnabled(
  state: CompatibilityPolicyOperationalApplication,
  surface: CompatibilitySurface,
): boolean {
  return state[surface] === true;
}

export function anyConfigurationSurfaceEnabled(
  state: CompatibilityPolicyOperationalApplication,
): boolean {
  return (
    state.audit || state.preview || state.validation || state.save
  );
}

export function cloneOperationalApplication(
  state: CompatibilityPolicyOperationalApplication,
): CompatibilityPolicyOperationalApplication {
  return { ...state };
}

export function applySurfaceToggle(
  state: CompatibilityPolicyOperationalApplication,
  surface: CompatibilitySurface,
  enabled: boolean,
): CompatibilityPolicyOperationalApplication {
  if (surface === 'runtime' && enabled) {
    throw new Error('Runtime promotion is not available in Phase 1b.8F');
  }
  const next = cloneOperationalApplication(state);
  next[surface] = enabled;
  next.runtime = false;
  return next;
}

export type SequenceAdvisory = {
  recommendedPrior: ConfigurationPromotionSurface[];
  missingPrior: ConfigurationPromotionSurface[];
  /** Soft: validation prefers preview promoted; save prefers audit+preview+validation. */
  requiresWaiverForMissingPrior: boolean;
};

export function sequenceAdvisoryFor(
  surface: ConfigurationPromotionSurface,
  current: CompatibilityPolicyOperationalApplication,
): SequenceAdvisory {
  const idx = PREFERRED_PROMOTION_ORDER.indexOf(surface);
  const recommendedPrior = PREFERRED_PROMOTION_ORDER.slice(0, idx);
  const missingPrior = recommendedPrior.filter((s) => !current[s]);
  const requiresWaiverForMissingPrior =
    (surface === 'validation' && missingPrior.includes('preview'))
    || (surface === 'save' && missingPrior.length > 0);
  return { recommendedPrior, missingPrior, requiresWaiverForMissingPrior };
}

export function readinessReadyFromChecks(checks: PromotionReadinessCheck[]): boolean {
  return checks.every((c) => c.status !== 'fail');
}

/** Codes that mean readiness could not be conclusively established (fail closed → unavailable). */
export const READINESS_UNAVAILABLE_CHECK_CODES = new Set([
  'preview_failed',
  'preview_unavailable',
  'telemetry_eval_failed',
  'rollout_state_unhealthy',
  'runtime_not_promotable',
]);

export type PromotionReadinessStatus = 'ready' | 'not_ready' | 'unavailable';

/**
 * Fail-closed status: never report ready when any check failed.
 * Distinguishes evidence-based not_ready from evaluator/pipeline unavailable.
 */
export function promotionReadinessStatusFromChecks(
  checks: PromotionReadinessCheck[],
): PromotionReadinessStatus {
  const fails = checks.filter((c) => c.status === 'fail');
  if (fails.length === 0) return 'ready';
  if (fails.some((c) => READINESS_UNAVAILABLE_CHECK_CODES.has(c.code))) {
    return 'unavailable';
  }
  return 'not_ready';
}

export function collectBlockersAndWarnings(checks: PromotionReadinessCheck[]): {
  blockers: string[];
  warnings: string[];
} {
  const blockers: string[] = [];
  const warnings: string[] = [];
  for (const c of checks) {
    if (c.status === 'fail') blockers.push(`${c.code}: ${c.summary}`);
    else if (c.status === 'warn') warnings.push(`${c.code}: ${c.summary}`);
  }
  return { blockers, warnings };
}

/**
 * Explicit positive readiness required for promotion.
 * Never treat null/undefined/missing ready as permissive.
 */
export function isPositivePromotionReadiness(input: {
  status?: PromotionReadinessStatus | null;
  ready?: boolean | null;
  blockers?: string[] | null;
}): boolean {
  return (
    input.status === 'ready'
    && input.ready === true
    && Array.isArray(input.blockers)
    && input.blockers.length === 0
  );
}
